import { useTranslation } from 'react-i18next';
import { Fan, Flame, PlugZap, Sun } from 'lucide-react';
import { MODALITA_SISTEMA, type ModalitaSistema } from '@emacomfort/domain';
import { useComando, useDevice, useObsoleto, useStato } from '@/app/DeviceProvider';
import { useFormato } from '@/app/formato';
import { Avviso, Card, Pagina, Segmenti, StatoComandoVista, Stepper, stili } from '@/ui';
import { useValoreDifferito, useZone } from '../comune';
import { ZoneCard } from './ZoneCard';
import s from '../features.module.css';

export function ZonePage() {
  const { t } = useTranslation();
  const f = useFormato();
  const { invia } = useDevice();
  const zone = useZone();
  const { obsoleto } = useObsoleto();
  const modalita = useStato((st) => st.stato?.modalita ?? st.impianto?.modalita ?? null);
  const comandoModalita = useComando('modalita');
  const comandoGlobale = useComando('globale');
  const richiesta = comandoModalita && comandoModalita.esito !== 'fallito' && comandoModalita.azione.azione === 'set_modalita' ? comandoModalita.azione.modalita : null;

  const setpointGlobale = zone.filter((z) => !z.sovrascritta)[0]?.setpoint ?? zone[0]?.setpoint ?? 20;
  const globale = useValoreDifferito(setpointGlobale, (v) => invia({ azione: 'set_setpoint_globale', valore: v }));
  const limiti = zone[0]?.limiti;

  const icone: Record<ModalitaSistema, React.ReactNode> = {
    RISCALDAMENTO: <Flame size={22} aria-hidden />,
    RAFFRESCAMENTO: <Fan size={22} aria-hidden />,
    AUTOMATICO: <Sun size={22} aria-hidden />,
    SPENTO: <PlugZap size={22} aria-hidden />,
  };

  return (
    <Pagina titolo={t('zone.titolo')}>
      <div className={s.colonne}>
        <Card titolo={t('zone.principali')}>
          <div>
            <div className={stili.etichetta}>{t('modalita.titolo')}</div>
          </div>
          <Segmenti
              colonne={2}
              etichetta={t('modalita.titolo')}
              valore={richiesta ?? modalita}
              onChange={(m) => void invia({ azione: 'set_modalita', modalita: m })}
              opzioni={MODALITA_SISTEMA.map((m) => ({ valore: m, etichetta: t(`modalita.${m}`), icona: icone[m] }))}
              disabilitato={obsoleto}
            />
          <StatoComandoVista esito={comandoModalita?.esito} motivo={comandoModalita?.motivo} />
          <div className={s.impostazione}>
            <div>
              <div className={stili.etichetta}>{t('zone.globale')}</div>
              <div className={s.letturaNota}>{t('zone.globaleNota')}</div>
              {limiti && <div className={s.letturaNota}>{t('zone.limite', { min: f.temp(limiti.min, 0), max: f.temp(limiti.max, 0), chi: limiti.impostatiDa })}</div>}
            </div>
          </div>
          <Stepper
            dimensione="primario"
            valore={f.tempNumero(globale.valore)}
            onMeno={() => globale.imposta(Math.max(limiti?.min ?? 5, globale.valore - 0.5))}
            onPiu={() => globale.imposta(Math.min(limiti?.max ?? 35, globale.valore + 0.5))}
            etichettaMeno={t('zone.meno', { zona: t('zone.globale') })}
            etichettaPiu={t('zone.piu', { zona: t('zone.globale') })}
            disabilitato={obsoleto || zone.length === 0}
          />
          <StatoComandoVista esito={globale.inAttesa && !comandoGlobale ? 'in_invio' : comandoGlobale?.esito} motivo={comandoGlobale?.motivo} />
        </Card>
        <div>
          <h2 className={stili.cardTitolo} style={{ marginBottom: 12 }}>
            {t('zone.individuali')}
          </h2>
          {zone.length === 0 ? (
            <Avviso tono="info">{t('zone.nessuna')}</Avviso>
          ) : (
            <div className={s.grigliaZone}>
              {[...zone]
                .sort((a, b) => a.ordinamento - b.ordinamento)
                .map((z) => (
                  <ZoneCard key={z.id} zona={z} />
                ))}
            </div>
          )}
        </div>
      </div>
    </Pagina>
  );
}
